import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import Button from 'common/components/button/Button';
import styles from './MaintenanceComponentsDetails.module.scss';
import InputError from 'common/components/inputError/InputError';
import Input from 'common/components/input/Input';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import { useForm } from 'react-hook-form';
import { DEFAULT_INPUT_RULES_WITH_REQUIRED, LOGGER_LOG_TYPE } from 'Config';
import GreyArrow from 'assets/svg/Arrow-grey.svg';
import { useEffect, useState } from 'react';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import React from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { MaintenanceComponentDto } from 'api/maintenanceComponents/models/MaintenanceComponentDto';
import MaintenanceComponentsService from 'api/maintenanceComponents/MaintenanceComponentsService';
import { useHistory, useParams } from 'react-router-dom';

type Props = {
};

const MaintenanceComponentsDetails: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    // eslint-disable-next-line
    let { type, itemId } = useParams<{ type: string, itemId: string }>();
    const [maintenanceComponent, setMaintenanceComponent] = useState<MaintenanceComponentDto | null>(null);

    const [itemToRemove, setItemToRemove] = useState<MaintenanceComponentDto>();
    const [dialogDeleteItemIsOpen, setDialogDeleteItemIsOpen] = React.useState(false);

    const form = useForm<MaintenanceComponentDto>({ shouldUnregister: false });
    const { register, handleSubmit, reset, errors } = form;

    const onSubmit = async (formData: MaintenanceComponentDto) => {
        try {
            Loading.show();

            if (maintenanceComponent != null) {
                maintenanceComponent.name = formData.name;
                await MaintenanceComponentsService.update(maintenanceComponent);
            } else {
                itemId = await MaintenanceComponentsService.create(formData);
                getData();
            }

            Loading.hide();
            addToast(t('common.messages.record_save_success'), {
                appearance: 'success',
            });

            history.goBack();
        } catch (error) {
            Loading.hide();

            if (error?.response?.status === 409) {
                addToast(t('common.messages.name_already_exists'), { appearance: 'warning' });
                return;
            }

            addToast(t('common.messages.record_save_error'), {
                appearance: 'error',
            });

            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't create or update maintenance component`,
                error
            );
        }
    };

    const getData = async () => {
        if (itemId) {
            try {
                Loading.show();

                const result = await MaintenanceComponentsService.getById(itemId);
                setMaintenanceComponent(result);
                reset(result);

                Loading.hide();
            }
            catch (error) {
                addToast(t('common.messages.error_load_info'), {
                    appearance: 'error',
                });

                Logger.error(
                    LOGGER_LOG_TYPE.REQUEST,
                    `Couldn't get maintenance component`,
                    error
                );
                Loading.hide();
            }
        }
    }

    const showRemoveItemDialog = (item: MaintenanceComponentDto) => {
        setItemToRemove(item);
        setDialogDeleteItemIsOpen(true);
    };

    const removeItem = async () => {
        setDialogDeleteItemIsOpen(false);
        if (!!itemToRemove) {
            try {
                Loading.show();
                await MaintenanceComponentsService.remove(itemToRemove);
                addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
                history.push(`/settings/maintenanceComponents/list`);
                Loading.hide();
            } catch (error) {
                addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
                Loading.hide();
            }
        }
    };

    useEffect(() => {
        getData();
    }, []);


    const onGoBack = () => {
        if (maintenanceComponent) {
            reset(maintenanceComponent);
        }
        history.goBack();
    }

    return (
        <div>
            <div className={styles.pageNameContainer}>
                <div className={styles.pageNameContent} onClick={() => history.push(`/settings/maintenanceComponents/list`)} >
                    <img className={styles.pageNameContentImage} src={GreyArrow} />
                    <span className={styles.pageNameContentText}>
                        {t('settings.maintenance_components')}
                    </span>
                </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <Row>
                    <Col xs={12} sm={6}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('maintenance_components.name')} {type !== 'details' ? '*' : ''}
                            </Label>
                            <Input
                                name='name'
                                placeholder={t('maintenance_components.name')}
                                defaultValue={maintenanceComponent?.name}
                                ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED })}
                                disabled={type === 'details'}
                            />
                            <InputError error={errors.name} maxLength={250} />
                        </FormItem>
                    </Col>
                </Row>

                <div className={styles.buttonContainer}>
                    <Button preset={'secondary'}
                        type='button'
                        text={t('common.cancel')}
                        onClick={() => onGoBack()} />
                    {type === 'details' &&
                        <Button
                            type="button"
                            text={t('common.remove')}
                            preset={'danger'}
                            onClick={() => showRemoveItemDialog({ id: maintenanceComponent?.id } as MaintenanceComponentDto)} />
                    }
                    {type === 'details' && <Button type='button' text={t('common.edit')} onClick={() => history.push(`/settings/maintenanceComponents/edit/${itemId}`)} />}
                    {type !== 'details' && <Button type='submit' text={t('common.save')} />}

                    <QuestionYesNo message={t('common.messages.remove_maintenance_component', { name: maintenanceComponent?.name ?? '' })}
                        isVisible={dialogDeleteItemIsOpen}
                        onYes={() => removeItem()}
                        onNo={() => setDialogDeleteItemIsOpen(false)} />
                </div>
            </form>
        </div>
    );
};

export default MaintenanceComponentsDetails;
